import { ItemStatusEnum } from '@/modules/common';
import { EventPrinterStatus } from '@/modules/events/constants';
import { positionOptions } from '@/modules/events/constants';
import { getModelGcrFileDetails } from '@/modules/events/helpers';
import { Model } from '@/modules/models/types';

export const prepareModelOptions = (models: Model[] = []) => {
  const filteredModels = models.filter(
    (model) => model.status === ItemStatusEnum.Ready
  );

  return filteredModels.map((model) => ({
    value: model.name,
    id: model.id,
    sides: model.fileName?.sides,
  }));
};

const prepareGcrFilesMapping = (
  formFields,
  formPrinters,
  models,
  printers,
  gcrs
) => {
  const filteredPrinters = formPrinters.filter(
    (printer) =>
      printer.printerId && printer.status === EventPrinterStatus.Enabled
  );

  const preparedFormFields = formFields
    .map(({ modelId, sizes }) => {
      return sizes.map((size) => ({
        modelId,
        sizeId: size.sizeId,
      }));
    })
    .flat();

  const gcrModelTypeMapping = Object.fromEntries(
    preparedFormFields
      .filter((field) => field.modelId && field.sizeId)
      .map((field) => {
        const { typeId, emptyGcrFilePrinter, isShoe } = getModelGcrFileDetails({
          models,
          printers,
          sizeId: field.sizeId,
          modelId: field.modelId,
          gcrs: gcrs.items,
          eventPrinters: filteredPrinters,
        });

        return [
          `${field.modelId}_${field.sizeId}`,
          { typeId, emptyGcrFilePrinter, isShoe },
        ];
      })
  );

  return {
    filteredPrinters,
    gcrModelTypeMapping,
  };
};

const checkSizeGcrFiles = (
  formField,
  filteredPrinters,
  gcrModelTypeMapping
) => {
  return formField.sizes.map((size) => {
    const { typeId, emptyGcrFilePrinter, isShoe } =
      gcrModelTypeMapping[`${formField.modelId}_${size.sizeId}`] ?? {};

    if (isShoe) {
      size.gcrFiles = {
        action: 'notNeeded',
      };
      return size;
    }

    if (!filteredPrinters.length) {
      size.gcrFiles = {
        action: 'redirect',
      };
    }

    size.gcrFiles = emptyGcrFilePrinter
      ? {
          action: 'create',
          typeId,
          printerValue: emptyGcrFilePrinter.value,
          printerId: emptyGcrFilePrinter.id,
        }
      : {
          action: 'none',
        };

    return size;
  });
};

const checkGcrFiles = (sizeGcrFiles) => {
  const notNeeded = sizeGcrFiles.filter(
    (size) => size.gcrFiles.action === 'notNeeded'
  );

  if (notNeeded.length) {
    return {
      action: 'notNeeded',
    };
  }

  const create = sizeGcrFiles.filter(
    (size) => size.gcrFiles.action === 'create'
  );

  if (create.length) {
    return {
      action: 'create',
      printers: create.map((size) => size.gcrFiles.printerValue),
    };
  }

  const redirect = sizeGcrFiles.filter(
    (size) => size.gcrFiles.action === 'redirect'
  );

  if (redirect.length) {
    return {
      action: 'redirect',
    };
  }

  const none = sizeGcrFiles.filter((size) => size.gcrFiles.action === 'none');

  if (none.length) {
    return {
      action: 'none',
    };
  }

  return null;
};

const checkPosition = (formField, modelOptions) => {
  const { modelId: selectedModelId, position: selectedPositions } = formField;

  const selectedModelOption = modelOptions?.find(
    (option) => option.id === selectedModelId
  );

  const availablePositions = positionOptions.filter((option) =>
    selectedModelOption?.sides.includes(option.value)
  );

  const matchingPositions = availablePositions.filter((option) =>
    selectedPositions.includes(option.value)
  );

  return selectedPositions.length !== matchingPositions.length
    ? matchingPositions.map((option) => option.value)
    : selectedPositions;
};

export const prepareFields = ({ form, options, data }) => {
  const { formFields, formPrinters } = form;
  const { models, printers, gcrs } = data;

  if (!formFields || formFields?.length === 0) return [];

  const { filteredPrinters, gcrModelTypeMapping } = prepareGcrFilesMapping(
    formFields,
    formPrinters,
    models,
    printers,
    gcrs
  );

  const fields = formFields.map((formField, index) => {
    const sizeGcrFiles = checkSizeGcrFiles(
      formField,
      filteredPrinters,
      gcrModelTypeMapping
    );

    return {
      ...formField,
      sizes: sizeGcrFiles,
      position: checkPosition(formField, options.modelOptions),
      modelIndex: index,
      gcrFiles: checkGcrFiles(sizeGcrFiles),
    };
  });

  return fields;
};
